import { getInspection, getInspectionAdjustedGrades, getInspectionAssetsByElementIds, getRecalcualteAdjustedGrades, doUpdateInspectionAdjustedGrades } from "../../services/inspectionService";
import { saveInspectionGrades } from "../../services/gradingService";
import { getInspectionPhotoFolders } from "../../utils/inspectionHelper";
import { selectInspectionId, selectPhotoFolders } from "../selectors/inspectionSelectors";
import { setInspection, setError, setLoading, setPhotoFolders, setImages, setLoadingImages, setErrorImages, setAdjustedGrades, setErrorAdjustedGrades, setLoadingAdjustedGrades, setSavingGrades, setErrorSavingGrades } from "../slices/inspectionSlice";
import { AppThunk } from "../store";
import { fetchDefectCodes, fetchDefects } from "./defectThunks";
import { fetchPositiveItems } from "./positiveItemThunks";
import { AdjustedGrade, SectionGradeUpdate } from "../../types/inspection";

export const fetchInspection = (inspectionId: string): AppThunk<Promise<boolean>> => async (dispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(false));

    try {
        const response = await getInspection(inspectionId);

        if (response) {
            dispatch(setInspection(response));

            dispatch(setPhotoFolders(getInspectionPhotoFolders(response)));
        } else {
            throw Error('no data');
        }
    } catch (e) {
        console.error(e);
        dispatch(setError(true));
    } finally {
        dispatch(setLoading(false));
    }

    return true;
}

export const fetchInspectionImages = (inspectionId: string): AppThunk => async (dispatch, getState) => {
    const photoFolders = selectPhotoFolders(getState());

    if (photoFolders.length) {
        dispatch(setLoadingImages(true));
        dispatch(setErrorImages(false));
    
        try {
            const elementIds = photoFolders.map((photoFolder) => photoFolder.Id);
            const response = await getInspectionAssetsByElementIds(inspectionId, elementIds);

            if (response) {
                dispatch(setImages(response));
            } else {
                throw Error('no data');
            }
        } catch (e) {
            console.error(e);
            dispatch(setErrorImages(true));
        } finally {
            dispatch(setLoadingImages(false));
        }
    }
}

export const fetchInspectionAdjustedGrades = (inspectionId: string): AppThunk => async (dispatch) => {
    dispatch(setErrorAdjustedGrades(false));
    dispatch(setLoadingAdjustedGrades(true));
    
    try {
        const response = await getInspectionAdjustedGrades(inspectionId);

        if (response) {
            dispatch(setAdjustedGrades(response));
        } else {
            dispatch(setAdjustedGrades(undefined));
        }
    } catch (error) {
        console.error(error);
        dispatch(setErrorAdjustedGrades(true));
    } finally {
        dispatch(setLoadingAdjustedGrades(false));
    }
}

export const updateInspectionAdjustedGrades = (inspectionId: string, adjustedGrades: AdjustedGrade[]): AppThunk => async (dispatch) => {
    dispatch(setErrorAdjustedGrades(false));
    dispatch(setLoadingAdjustedGrades(true));
    
    try {
        const response = await doUpdateInspectionAdjustedGrades(inspectionId, adjustedGrades);

        if (response) {
            dispatch(setAdjustedGrades(response));
        } else {
            dispatch(setAdjustedGrades(undefined));
        }
    } catch (error) {
        console.error(error);
        dispatch(setErrorAdjustedGrades(true));
    } finally {
        dispatch(setLoadingAdjustedGrades(false));
    }

    return null;
}

export const fetchRecalculateAdjustedGrades = (inspectionId: string, adjustedGrades: AdjustedGrade[]): AppThunk => async (dispatch) => {
    dispatch(setErrorAdjustedGrades(false));
    dispatch(setLoadingAdjustedGrades(true));

    try {
        const response = await getRecalcualteAdjustedGrades(inspectionId, adjustedGrades);

        if (response) {
            dispatch(setAdjustedGrades(response));
        } else {
            dispatch(setAdjustedGrades(undefined));
        }
    } catch (error) {
        console.error(error);
        dispatch(setErrorAdjustedGrades(true));
    } finally {
        dispatch(setLoadingAdjustedGrades(false));
    }
}


export const updateInspectionSectionGrades = (inspectionId: string, grades: SectionGradeUpdate[]): AppThunk<Promise<boolean>> => async (dispatch) => {
    dispatch(setErrorSavingGrades(false));
    dispatch(setSavingGrades(true));

    try {
        const saved = await saveInspectionGrades(inspectionId, grades);

        if (!saved) {
            dispatch(setErrorSavingGrades(true));
        }

        return saved;
    } catch (error) {
        console.error(error);
        dispatch(setErrorSavingGrades(true));
        return false;
    } finally {
        dispatch(setSavingGrades(false));
    }
}

export const fetchInspectionData = (inspectionId: string): AppThunk => async (dispatch, getState) => {
    const currentInspectionId = selectInspectionId(getState());

    if (currentInspectionId?.toLowerCase() !== inspectionId) {
        dispatch(fetchDefects(inspectionId));

        dispatch(fetchPositiveItems(inspectionId));

        await dispatch(fetchInspection(inspectionId));

        dispatch(fetchInspectionImages(inspectionId));

        dispatch(fetchDefectCodes());

        dispatch(fetchInspectionAdjustedGrades(inspectionId));
    }

}